import {
    Box,
    Paper,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Iconify from 'src/components/iconify';
import { useGetSerpContentMutation } from 'src/context/api/competitorsMonitoring/api';
import { SerpModalProps } from './types';
import sanitizeSerpHtml from './sanitizeSerpHtml';
import getSerpErrorMessage from './getSerpErrorMessage';

function SerpModal({ open, onClose, payload, deviceType = 'desktop' }: SerpModalProps): JSX.Element {
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const [getSerpContent, { isLoading: isFetching }] = useGetSerpContentMutation();

    // Récupération du contenu SERP via l'API
    useEffect(() => {
        
        if (!open) {
            setLoading(false);
            return;
        }

        if (!payload.gcsUrl) {
            setError("Aucune URL SERP disponible pour cet élément");
            setLoading(false);
            return;
        }

        async function fetchSerpContent() {
            try {
                setLoading(true);
                setError(null);

                // Appel à l'API via RTK Query
                const data = await getSerpContent({ gcsUrl: payload.gcsUrl! }).unwrap();
                
                if (data.error) {
                    throw new Error(data.error);
                }


                // Nettoyage du HTML avant de créer le blob (défense en profondeur,
                // le backend nettoie déjà via clean_html)
                const cleanedHtml = sanitizeSerpHtml(data.html);

                // Créer un blob URL avec le HTML nettoyé
                const blob = new Blob([cleanedHtml], { type: "text/html" });
                const blobUrl = URL.createObjectURL(blob);
                setIframeSrc(blobUrl);

            } catch (err) {
                console.error("Erreur lors de la récupération du contenu SERP:", err);
                setError(getSerpErrorMessage(err));
            } finally {
                setLoading(false);
            }
        }

        fetchSerpContent();
    }, [open, payload.gcsUrl, getSerpContent]);

    // Nettoyage lors de la fermeture du modal
    useEffect(() => {
        if (!open) {
            if (iframeSrc && iframeSrc.startsWith('blob:')) {
                URL.revokeObjectURL(iframeSrc);
            }
            setIframeSrc(null);
            setError(null);
        }
    }, [open, iframeSrc]);

    // Configuration selon le type d'appareil
    const isMobile = deviceType === 'mobile';
    const modalWidth = isMobile ? '430px' : '100%';
    const modalHeight = isMobile ? '932px' : '600px';
    const carouselWidth = "100%";

    let content: JSX.Element | null = null;
    if (loading) {
        content = (
            <Box sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
                <Skeleton
                    variant="rectangular"
                    width={modalWidth}
                    height={modalHeight}
                    sx={{ borderRadius: "0px 0px 15px 15px" }}
                    animation="wave"
                />
            </Box>
        );
    } else if (error) {
        content = (
            <Box sx={{
                p: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px',
                color: 'error.main'
            }}>
                <Typography>{error}</Typography>
            </Box>
        );
    } else if (iframeSrc) {
        content = (
            <Box sx={{
                borderRadius: "0px 0px 15px 15px",
                width: "100%",
                display: 'flex',
                justifyContent: 'center',
                overflow: 'hidden',
                height: modalHeight
            }}>
                <iframe
                    src={iframeSrc}
                    title={iframeSrc}
                    style={{
                        width: carouselWidth,
                        height: "100%",
                        border: "1px solid transparent",
                        borderRadius: "0px 0px 15px 15px",
                    }}
                    onLoad={() => setLoading(false)}
                    onError={() => {
                        setError("Erreur lors du chargement de l'iframe");
                        setLoading(false);
                    }}
                />
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : "lg"}
            fullWidth={!isMobile}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh',
                    ...(isMobile && {
                        width: modalWidth,
                        maxWidth: modalWidth,
                        margin: 'auto'
                    })
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">
                            {isMobile ? 'Mobile' : 'Desktop'} SERP
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Search Term: {payload.searchTerm}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <IconButton onClick={onClose} size="small">
                        <Iconify icon="eva:close-fill" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {(() => {
                            // Extraire la date depuis l'URL GCS
                            const gcsUrl = payload.gcsUrl || '';
                            const dateMatch = gcsUrl.match(/date=(\d{4}-\d{2}-\d{2})/);
                            if (dateMatch) {
                                return new Date(dateMatch[1]).toLocaleDateString('fr-FR');
                            }
                            // Fallback sur startDate si pas de date dans l'URL
                            return new Date(payload.startDate).toLocaleDateString('fr-FR');
                        })()}
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                {content}
            </DialogContent>
        </Dialog>
    );
}

export default SerpModal;
